import React, { useState, useEffect } from 'react';
import CustomDateTimePicker from './CustomDateTimePicker';
import { calculateWorkingDays, addWorkingDays } from '../../utils/productionCalendar';
import './CustomDateRangePicker.css';

const CustomDateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel = "Дата начала",
  endLabel = "Дата окончания",
  required = false,
  showInputs = true, // Новый параметр для управления отображением полей ввода
  disabled = false,
  layout = 'horizontal', // horizontal | vertical
  compactCalculator = false,
  showFlowArrows = false,
  defaultDayType = 'calendar',
}) => {
  const [period, setPeriod] = useState({
    days: 0,
    workingDays: 0,
    weeks: 0,
    months: 0,
    years: 0
  });
  
  const [periodType, setPeriodType] = useState('days');
  const [periodValue, setPeriodValue] = useState('');
  const [dayType, setDayType] = useState(defaultDayType); // 'calendar' или 'working'

  // Вычисление разницы между датами
  const calculatePeriod = async (start, end) => {
    if (!start || !end) return { days: 0, workingDays: 0, weeks: 0, months: 0, years: 0 };
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (endDate < startDate) return { days: 0, workingDays: 0, weeks: 0, months: 0, years: 0 };
    
    const diffTime = Math.abs(endDate - startDate);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Вычисляем рабочие дни
    let workingDays = 0;
    try {
      workingDays = await calculateWorkingDays(startDate, endDate);
    } catch (error) {
      console.warn('Ошибка подсчета рабочих дней:', error);
      // Fallback: примерно 5/7 от общего количества дней
      workingDays = Math.floor(days * 5 / 7);
    }
    
    // Вычисляем месяцы и годы более точно
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth();
    
    const years = Math.floor(months / 12);
    months = months % 12;
    
    // Корректировка для дней
    if (endDate.getDate() < startDate.getDate()) {
      months--;
      if (months < 0) {
        months = 11;
      }
    }
    
    return {
      days: days,
      workingDays: workingDays,
      weeks: Math.floor(days / 7),
      months: months,
      years: years
    };
  };

  // Обновление периода при изменении дат
  useEffect(() => {
    const updatePeriod = async () => {
      const newPeriod = await calculatePeriod(startDate, endDate);
      setPeriod(newPeriod);
    };
    
    updatePeriod();
  }, [startDate, endDate]);

  // Расчет конечной даты на основе периода
  const calculateEndDate = async (start, value, type, useWorkingDays = false) => {
    if (!start || !value) return '';
    
    const startDate = new Date(start);
    let endDate = new Date(start);
    
    switch(type) {
      case 'days':
        if (useWorkingDays) {
          try {
            endDate = await addWorkingDays(startDate, parseInt(value));
            if (!endDate) {
              endDate = new Date(start);
              endDate.setDate(startDate.getDate() + parseInt(value));
            }
          } catch (error) {
            console.warn('Ошибка расчета рабочих дней:', error);
            endDate.setDate(startDate.getDate() + parseInt(value));
          }
        } else {
          endDate.setDate(startDate.getDate() + parseInt(value));
        }
        break;
      case 'weeks':
        endDate.setDate(startDate.getDate() + (parseInt(value) * 7));
        break;
      case 'months':
        endDate.setMonth(startDate.getMonth() + parseInt(value));
        break;
      case 'years':
        endDate.setFullYear(startDate.getFullYear() + parseInt(value));
        break;
      default:
        break;
    }
    
    const pad = (v) => String(v).padStart(2, '0');
    return `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
  };

  // Обновление конечной даты при изменении периода
  const handlePeriodValueChange = async (value) => {
    setPeriodValue(value);
    if (startDate && value) {
      const newEndDate = await calculateEndDate(startDate, value, periodType, dayType === 'working');
      if (newEndDate) {
        onEndDateChange({ target: { value: newEndDate } });
      }
    }
  };

  const renderCalculator = () => (
    <div className={`period-flow ${showFlowArrows ? 'period-flow--arrows' : ''}`}>
      <div className={`period-calculator ${compactCalculator ? 'period-calculator--compact' : ''}`}>
        <div className="period-display">
          <div className="period-info">
            <span className="period-label">Период:</span>
            <div className="period-values">
              {period.years > 0 && <span className="period-item">{period.years} г.</span>}
              {period.months > 0 && <span className="period-item">{period.months} мес.</span>}
              {period.weeks > 0 && <span className="period-item">{period.weeks} нед.</span>}
              {period.days % 7 > 0 && <span className="period-item">{period.days % 7} дн.</span>}
            </div>
          </div>
          
          <div className="working-days-toggle">
            <button
              type="button"
              className={`toggle-btn ${dayType === 'calendar' ? 'active' : ''}`}
              onClick={() => setDayType('calendar')}
              disabled={disabled}
            >
              📅 {period.days} дн.
            </button>
            <button
              type="button"
              className={`toggle-btn ${dayType === 'working' ? 'active' : ''}`}
              onClick={() => setDayType('working')}
              disabled={disabled}
            >
              💼 {period.workingDays} раб.дн.
            </button>
          </div>
        </div>

        <div className="period-calculator-controls">
          <div className="calculator-row">
            <span className="calculator-label">Рассчитать:</span>
            <input
              type="number"
              value={periodValue}
              onChange={(e) => handlePeriodValueChange(e.target.value)}
              placeholder="Количество"
              className="period-input"
              min="1"
              disabled={disabled}
            />
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="period-type-select"
              disabled={disabled}
            >
              <option value="days">дней</option>
              <option value="weeks">недель</option>
              <option value="months">месяцев</option>
              <option value="years">лет</option>
            </select>
            <span className="calculator-suffix">от начальной даты</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="custom-date-range-picker">
      {showInputs && (
        <div className="custom-date-inputs">
          {layout === 'vertical' ? (
            <div className="custom-date-inputs-vertical">
              <CustomDateTimePicker
                value={startDate}
                onChange={onStartDateChange}
                label={startLabel}
                required={required}
                max={endDate}
                disabled={disabled}
              />
              {renderCalculator()}
              <CustomDateTimePicker
                value={endDate}
                onChange={onEndDateChange}
                label={endLabel}
                required={required}
                min={startDate}
                disabled={disabled}
              />
            </div>
          ) : (
            <div className="custom-date-inputs-row">
              <div className="custom-date-inputs-col custom-date-inputs-col--start">
                <CustomDateTimePicker
                  value={startDate}
                  onChange={onStartDateChange}
                  label={startLabel}
                  required={required}
                  max={endDate}
                  disabled={disabled}
                />
              </div>

              <div className="date-flow-arrow" aria-hidden="true">→</div>

              {renderCalculator()}

              <div className="date-flow-arrow" aria-hidden="true">→</div>
              
              <div className="custom-date-inputs-col custom-date-inputs-col--end">
                <CustomDateTimePicker
                  value={endDate}
                  onChange={onEndDateChange}
                  label={endLabel}
                  required={required}
                  min={startDate}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
